// The money terms. Noctant charges none of them and pays none of them out:
// a fee is the price the offer displays, and the payouts follow the
// settlement the template is sold under (simulated, for every template the
// builder writes).

import { Show } from 'solid-js';

import { sponsorshipLine } from '../core/ruleText';
import type { CustomChallengeBuilderInput } from '../form';
import Dropdown from './Dropdown';
import NumberInput from './NumberInput';
import { useMoneyMark, useTemplatePayoutWords } from './builderWordsContext';

interface PricingCardProps {
  form: CustomChallengeBuilderInput;
  /** Say whether the template runs on an account the firm sponsors. */
  sponsorship?: boolean;
  onUpdate: <K extends keyof CustomChallengeBuilderInput>(key: K, value: CustomChallengeBuilderInput[K]) => void;
}

export default function PricingCard(props: PricingCardProps) {
  const payout = useTemplatePayoutWords();
  const mark = useMoneyMark();
  return (
    <section class="ch-card">
      <h3 class="ch-card-title">Simulated pricing</h3>
      <div class="ch-grid-3">
        <div class="ch-field">
          <span class="ch-label">Simulated fee</span>
          <NumberInput
            value={props.form.challengePrice}
            onInput={(value) => props.onUpdate('challengePrice', value)}
            prefix={mark}
            min={0}
            step={1}
            placeholder="free"
          />
        </div>
        <div class="ch-field">
          <span class="ch-label">Simulated fee charged</span>
          <Dropdown
            value={props.form.billingPeriod}
            options={[
              { value: 'one_time', label: 'Once' },
              { value: 'monthly', label: 'Every month' },
            ]}
            onChange={(value) => props.onUpdate('billingPeriod', value === 'monthly' ? 'monthly' : 'one_time')}
          />
        </div>
        <div class="ch-field">
          <span class="ch-label">{payout.payout_label} split</span>
          <NumberInput
            value={props.form.profitSplitPct}
            onInput={(value) => props.onUpdate('profitSplitPct', value)}
            min={0}
            max={100}
            step={5}
          />
        </div>
        <div class="ch-field">
          <span class="ch-label">Simulated fee refunded with the first {payout.payout}</span>
          <NumberInput
            value={props.form.feeRefundPct}
            onInput={(value) => props.onUpdate('feeRefundPct', value)}
            min={0}
            max={100}
            step={5}
            placeholder="never"
          />
        </div>
        <div class="ch-field">
          <span class="ch-label">Funded account</span>
          <Dropdown
            value={props.form.requiresActivation ? 'required' : 'none'}
            options={[
              { value: 'required', label: 'Needs activating' },
              { value: 'none', label: 'Starts on its own' },
            ]}
            onChange={(value) => props.onUpdate('requiresActivation', value === 'required')}
          />
        </div>
        <Show when={props.form.requiresActivation}>
          <div class="ch-field">
            <span class="ch-label">Simulated activation fee</span>
            <NumberInput
              value={props.form.activationPrice}
              onInput={(value) => props.onUpdate('activationPrice', value)}
              prefix={mark}
              min={0}
              step={1}
            />
          </div>
        </Show>
      </div>
      {/* The attach's own rule, in its words: the firm decides, the console tells. */}
      <Show when={props.sponsorship}>
        <p class="ch-sponsorship" data-testid="ch-sponsorship">{sponsorshipLine(props.form)}</p>
      </Show>
    </section>
  );
}
